import React from 'react';
import { Link } from 'react-router-dom';

const HomePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4">
      <div className="max-w-4xl w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <img
              src="/gympe.svg"
              alt="Gympe"
              className="h-16 w-auto"
            />
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-zinc-950 mb-4 tracking-tight">
            Welcome to <span className="text-[#AAD816]">Gympe</span>
          </h1>
          <p className="text-xl text-zinc-600 mb-8 max-w-2xl mx-auto font-medium">
            The complete gym management solution. Manage members, track attendance,
            and grow your fitness business with ease.
          </p>
        </div>

        {/* Action Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
          {/* Sign Up Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-zinc-100 hover:border-[#AAD816]/30 transition-all duration-300 group">
            <div className="w-16 h-16 bg-[#AAD816]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-zinc-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-zinc-950 mb-3">New to Gympe?</h3>
            <p className="text-zinc-600 mb-6 font-medium">
              Start your gym management journey. Create your account and set up your gym in minutes.
            </p>
            <Link
              to="/register"
              className="w-full bg-zinc-950 text-white py-4 px-6 rounded-xl font-bold hover:bg-[#AAD816] hover:text-zinc-950 transition-all duration-300 inline-block shadow-lg shadow-zinc-200"
            >
              Get Started Free
            </Link>
            <p className="text-xs text-zinc-400 mt-4 font-bold uppercase tracking-widest">No credit card required</p>
          </div>

          {/* Sign In Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center border border-zinc-100 hover:border-[#AAD816]/30 transition-all duration-300 group">
            <div className="w-16 h-16 bg-zinc-50 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
              <svg className="w-8 h-8 text-zinc-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-zinc-950 mb-3">Already have an account?</h3>
            <p className="text-zinc-600 mb-6 font-medium">
              Welcome back! Sign in to access your gym dashboard and manage your business.
            </p>
            <Link
              to="/login"
              className="w-full bg-white text-zinc-950 py-4 px-6 rounded-xl font-bold border-2 border-zinc-950 hover:bg-zinc-50 transition-all duration-300 inline-block"
            >
              Sign In
            </Link>
          </div>
        </div>

        {/* Features Preview */}
        <div className="mt-20 text-center">
          <h2 className="text-sm font-black text-zinc-400 uppercase tracking-[0.2em] mb-12">Why Choose Gympe?</h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center group">
              <div className="w-14 h-14 bg-white shadow-sm rounded-xl flex items-center justify-center mx-auto mb-6 border border-zinc-100 group-hover:border-[#AAD816]/50 transition-colors">
                <svg className="w-6 h-6 text-zinc-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-zinc-950 mb-2">Member Management</h3>
              <p className="text-zinc-600 text-sm font-medium">Easily manage member profiles, memberships, and payments</p>
            </div>
            <div className="text-center group">
              <div className="w-14 h-14 bg-white shadow-sm rounded-xl flex items-center justify-center mx-auto mb-6 border border-zinc-100 group-hover:border-[#AAD816]/50 transition-colors">
                <svg className="w-6 h-6 text-zinc-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h3 className="font-bold text-zinc-950 mb-2">Analytics & Reports</h3>
              <p className="text-zinc-600 text-sm font-medium">Track performance with detailed analytics and reports</p>
            </div>
            <div className="text-center group">
              <div className="w-14 h-14 bg-white shadow-sm rounded-xl flex items-center justify-center mx-auto mb-6 border border-zinc-100 group-hover:border-[#AAD816]/50 transition-colors">
                <svg className="w-6 h-6 text-zinc-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-zinc-950 mb-2">Attendance Tracking</h3>
              <p className="text-zinc-600 text-sm font-medium">Monitor member attendance and engagement</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage; 